import { useAppSelector } from '@/hooks'
import {
  programsNewCollection,
  selectProgramRewardsByDeviceType,
  selectProgramRewardsByCategory,
  ProgramRewardsByDeviceType,
} from '@/reducers/programsNew'
import {
  ProgramRewardsByCategory,
  ProgramRewardsType,
} from '@/types/programs-new/programs-new'
import { useMemo } from 'react'

export type DeviceIncentives = {
  vehicle: number
  thermostat: number
  charger: number
}

function getDeviceIncentivesExist(
  rewardsByDeviceType: ProgramRewardsByDeviceType | null,
) {
  if (rewardsByDeviceType == null) {
    return false
  }

  return Object.values(rewardsByDeviceType).some((rewards) =>
    rewards.some(
      (r) => (r.dollarAmount ?? 0) + (r.dollarAmountYearly ?? 0) > 0,
    ),
  )
}

function getCategoricalIncentivesExist(
  rewardsByCategory: ProgramRewardsByCategory | null,
) {
  if (rewardsByCategory == null) {
    return false
  }

  return Object.values(rewardsByCategory).some((rewards) =>
    rewards.some(
      (r) => (r.dollarAmount ?? 0) + (r.dollarAmountYearly ?? 0) > 0,
    ),
  )
}

function getIncentivesExist(
  rewardsByDeviceType: ProgramRewardsByDeviceType | null,
  rewardsByCategory: ProgramRewardsByCategory | null,
) {
  return (
    getDeviceIncentivesExist(rewardsByDeviceType) ||
    getCategoricalIncentivesExist(rewardsByCategory)
  )
}

export default function useUtilityProgramRewards(programId: number) {
  const { isLoadingOrIdle: isLoading } = programsNewCollection.useFetch()
  const rewardsByCategory = useAppSelector(
    selectProgramRewardsByCategory(programId),
  )
  const rewardsByDeviceType = useAppSelector(
    selectProgramRewardsByDeviceType(programId),
  )
  const applicableDeviceIncentiveRewardTypes: ProgramRewardsType[] = [
    'enrollment',
    'participation',
    'application',
    'endOfProgram',
  ]
  const deviceIncentives: DeviceIncentives = useMemo(
    () => ({
      vehicle: (
        rewardsByDeviceType?.vehicle?.filter((v) =>
          applicableDeviceIncentiveRewardTypes.includes(v.type),
        ) ?? []
      ).reduce(
        (total, curr) =>
          total + (curr.dollarAmountYearly ?? curr.dollarAmount ?? 0),
        0,
      ),
      thermostat: (
        rewardsByDeviceType?.thermostat?.filter((t) =>
          applicableDeviceIncentiveRewardTypes.includes(t.type),
        ) ?? []
      ).reduce(
        (total, curr) =>
          total + (curr.dollarAmountYearly ?? curr.dollarAmount ?? 0),
        0,
      ),
      charger: (
        rewardsByDeviceType?.charger?.filter((c) =>
          applicableDeviceIncentiveRewardTypes.includes(c.type),
        ) ?? []
      ).reduce(
        (total, curr) =>
          total + (curr.dollarAmountYearly ?? curr.dollarAmount ?? 0),
        0,
      ),
    }),
    [rewardsByDeviceType],
  )

  const incentivesExist = getIncentivesExist(
    rewardsByDeviceType,
    rewardsByCategory,
  )

  return {
    rewardsByCategory,
    rewardsByDeviceType,
    deviceIncentives,
    isLoading,
    incentivesExist,
  }
}
